import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ROLES } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Building2, UserCheck } from 'lucide-react';

const TeamPage = ({ mode }) => {
  const { user, USERS: allUsers, visibleClients, visibleSessions, visibleRmIds } = useApp();
  const isAdmin = mode === 'admin';

  const salesHeads = allUsers.filter((u) => u.role === ROLES.SALES_HEAD);
  const rms = allUsers.filter((u) => u.role === ROLES.RM && (isAdmin || visibleRmIds.includes(u.user_id)));

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1400px] mx-auto">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{isAdmin ? 'Team — all roles' : 'My RMs'}</div>
        <h1 className="text-3xl font-bold text-brand-indigo">{isAdmin ? 'Sales Heads & Relationship Managers' : 'Relationship Managers'}</h1>
      </div>

      {isAdmin && (
        <Card className="border-brand-border">
          <CardContent className="p-0">
            <div className="px-5 py-4 border-b border-brand-border flex items-center gap-2">
              <Building2 className="h-4 w-4 text-brand-indigo" />
              <div className="text-sm font-semibold text-brand-indigo">Sales Heads</div>
            </div>
            <div className="divide-y divide-brand-border/50">
              {salesHeads.map((sh) => {
                const mapped = allUsers.filter((u) => u.role === ROLES.RM && sh.rm_ids?.includes(u.user_id));
                return (
                  <div key={sh.user_id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-brand-indigo">{sh.name}</div>
                      <div className="text-[11px] text-muted-foreground">{sh.email} · {sh.title}</div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {mapped.map((r) => (
                        <span key={r.user_id} className="text-[10px] font-semibold uppercase tracking-wider bg-brand-bg border border-brand-border text-brand-indigo px-2 py-0.5 rounded-full">{r.name}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-brand-border flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-brand-indigo" />
            <div className="text-sm font-semibold text-brand-indigo">Relationship Managers</div>
          </div>
          <div className="divide-y divide-brand-border/50">
            {rms.map((rm) => {
              const rmClients = visibleClients.filter((c) => c.rm_id === rm.user_id);
              const rmSessions = visibleSessions.filter((s) => s.rm_id === rm.user_id);
              const sh = allUsers.find((u) => u.user_id === rm.sales_head_id);
              return (
                <div key={rm.user_id} className="px-5 py-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-brand-indigo">{rm.name}</div>
                    <div className="text-[11px] text-muted-foreground">{rm.email} · EUIN {rm.euin} · Reports to {sh?.name || '—'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-brand-indigo border-brand-border">{rmClients.length} client{rmClients.length !== 1 && 's'}</Badge>
                    <Badge variant="outline" className="text-brand-indigo border-brand-border">{rmSessions.length} session{rmSessions.length !== 1 && 's'}</Badge>
                  </div>
                </div>
              );
            })}
            {rms.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">No RMs mapped.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPage;
